import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { Plus, Trash, PencilSimple, DownloadSimple, Users } from "@phosphor-icons/react";
import { startDownload, backendDownloadUrl, downloadFromBackend } from "../../lib/download";

const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export default function Dealers() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null); // null | {}-new-dealer | full dealer obj (edit)
  const [report, setReport] = useState(null);   // dealer to show report modal for
  const [reportRange, setReportRange] = useState({ from: daysAgo(30), to: today() });
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/dealers");
    setRows(data);
  };
  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers(data));
  }, []);

  const save = async () => {
    const body = {
      name: editing.name || "",
      contact_email: editing.contact_email || "",
      commission_cpl: parseFloat(editing.commission_cpl) || 0,
      notes: editing.notes || "",
      customer_ids: editing.customer_ids || [],
    };
    try {
      if (editing.dealer_id) await api.put(`/dealers/${editing.dealer_id}`, body);
      else await api.post("/dealers", body);
      toast.success(editing.dealer_id ? "Dealer updated" : "Dealer added");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete dealer "${d.name}"? (Their commission history will be lost — reports won't work.)`)) return;
    await api.delete(`/dealers/${d.dealer_id}`);
    toast.success("Dealer deleted");
    load();
  };

  const loadReport = async (dealer) => {
    setReport(dealer);
    setReportData(null); setReportLoading(true);
    try {
      const { data } = await api.get(`/dealers/${dealer.dealer_id}/commission-report`, {
        params: { date_from: reportRange.from, date_to: reportRange.to, fmt: "json" },
      });
      setReportData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setReportLoading(false); }
  };
  useEffect(() => {
    if (report) loadReport(report);
    /* eslint-disable-next-line */
  }, [reportRange.from, reportRange.to]);

  const downloadReport = async (fmt) => {
    try {
      const filename = await downloadFromBackend(`/dealers/${report.dealer_id}/commission-report`, {
        date_from: reportRange.from, date_to: reportRange.to, fmt,
      });
      toast.success(`Downloaded ${filename}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  const toggleCustomer = (cid) => {
    const cur = new Set(editing.customer_ids || []);
    if (cur.has(cid)) cur.delete(cid); else cur.add(cid);
    setEditing({ ...editing, customer_ids: Array.from(cur) });
  };

  return (
    <SectionPad>
      <PageHeader
        title="Dealers"
        subtitle="PARTNERS / COMMISSION"
        testId="dealers-header"
        actions={
          <button className="gf-btn gf-btn-primary"
            onClick={() => setEditing({ name: "", contact_email: "", commission_cpl: 1.0, notes: "", customer_ids: [] })}
            data-testid="add-dealer-button">
            <Plus size={14}/> Add dealer
          </button>
        }
      />
      <div className="gf-card">
        <table className="gf-table">
          <thead>
            <tr>
              <th>NAME</th><th>EMAIL</th><th>CUSTOMERS</th>
              <th className="text-right">CPL</th>
              <th className="text-right">VOLUME (L)</th>
              <th className="text-right">EARNED</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.dealer_id}>
                <td className="font-medium">{r.name}</td>
                <td className="text-xs">{r.contact_email}</td>
                <td className="text-xs">
                  {r.customer_count > 0 ? (
                    <Badge variant="info"><Users size={10}/> {r.customer_count} customer{r.customer_count !== 1 ? "s" : ""}</Badge>
                  ) : <span className="text-zinc-400">none</span>}
                </td>
                <td className="gf-num">{r.commission_cpl?.toFixed(2)}</td>
                <td className="gf-num">{r.total_volume_L?.toLocaleString()}</td>
                <td className="gf-num font-semibold">${r.commission_earned?.toFixed(2)}</td>
                <td className="text-right">
                  <button className="gf-btn gf-btn-ghost" onClick={() => loadReport(r)} title="Commission report"
                    data-testid={`report-dealer-${r.dealer_id}`}><DownloadSimple size={14}/></button>
                  <button className="gf-btn gf-btn-ghost" onClick={() => setEditing({ ...r })} title="Edit"
                    data-testid={`edit-dealer-${r.dealer_id}`}><PencilSimple size={14}/></button>
                  <button className="gf-btn gf-btn-ghost" onClick={() => del(r)} title="Delete"><Trash size={14}/></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-400 py-8">No dealers.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* ---- ADD / EDIT DEALER ---- */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-2xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>{editing.dealer_id ? "Edit dealer" : "New dealer"}</h3></div>
            <div className="gf-card-body space-y-4 overflow-auto">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="overline mb-1">Name</div>
                  <input className="gf-input" value={editing.name || ""}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="dealer-name" />
                </div>
                <div><div className="overline mb-1">Email</div>
                  <input className="gf-input" value={editing.contact_email || ""}
                    onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} />
                </div>
                <div><div className="overline mb-1">Commission (cents/L)</div>
                  <input type="number" step="0.01" className="gf-input" value={editing.commission_cpl ?? ""}
                    onChange={(e) => setEditing({ ...editing, commission_cpl: e.target.value })} data-testid="dealer-commission-cpl" />
                </div>
                <div><div className="overline mb-1">Notes</div>
                  <input className="gf-input" value={editing.notes || ""}
                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
                </div>
              </div>
              <div>
                <div className="overline mb-2 flex items-center justify-between">
                  <span>Assigned customers ({(editing.customer_ids || []).length})</span>
                  <span className="text-[10px] text-zinc-400 normal-case">Commission accrues on all volume from checked customers</span>
                </div>
                <div className="max-h-72 overflow-auto border border-zinc-200 divide-y divide-zinc-100">
                  {customers.map((c) => {
                    const checked = (editing.customer_ids || []).includes(c.customer_id);
                    return (
                      <label key={c.customer_id} className={`flex items-center gap-3 p-2 cursor-pointer text-sm hover:bg-zinc-50 ${checked ? "bg-blue-50" : ""}`}>
                        <input type="checkbox" checked={checked}
                          onChange={() => toggleCustomer(c.customer_id)}
                          data-testid={`assign-customer-${c.customer_id}`}/>
                        <span className="flex-1">{c.company_name}</span>
                        <span className="text-[10px] text-zinc-400 font-mono">{c.province}</span>
                      </label>
                    );
                  })}
                  {customers.length === 0 && <div className="p-3 text-zinc-400 text-xs">No customers yet.</div>}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={save} data-testid="save-dealer-button">
                {editing.dealer_id ? "Save changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- COMMISSION REPORT MODAL ---- */}
      {report && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setReport(null); setReportData(null); }}>
          <div className="bg-white w-full max-w-5xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header">
              <h3>Commission report — {report.name}</h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <span className="overline">FROM</span>
                  <input type="date" className="gf-input" value={reportRange.from}
                    onChange={(e) => setReportRange({ ...reportRange, from: e.target.value })} data-testid="dealer-report-from" />
                </div>
                <div className="flex items-center gap-1">
                  <span className="overline">TO</span>
                  <input type="date" className="gf-input" value={reportRange.to}
                    onChange={(e) => setReportRange({ ...reportRange, to: e.target.value })} data-testid="dealer-report-to" />
                </div>
                <button className="gf-btn gf-btn-outline" onClick={() => downloadReport("csv")} data-testid="dealer-report-csv">CSV</button>
                <button className="gf-btn gf-btn-primary" onClick={() => downloadReport("pdf")} data-testid="dealer-report-pdf">PDF</button>
              </div>
            </div>
            <div className="overflow-auto flex-1">
              {reportLoading ? (
                <div className="p-8 text-center font-mono text-sm text-zinc-500">BUILDING REPORT…</div>
              ) : !reportData || !reportData.rows.length ? (
                <div className="p-8 text-center text-zinc-400">No transactions in this period for {report.name}'s customers.</div>
              ) : (
                <>
                  <table className="gf-table">
                    <thead>
                      <tr>
                        <th>CUSTOMER</th>
                        <th className="text-right">TX</th>
                        <th className="text-right">VOLUME (L)</th>
                        <th className="text-right">REVENUE</th>
                        <th className="text-right">GROSS PROFIT</th>
                        <th className="text-right">CPL</th>
                        <th className="text-right">COMMISSION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.rows.map((r, i) => (
                        <tr key={i}>
                          <td className="font-medium">{r.CUSTOMER}</td>
                          <td className="gf-num">{r["TX COUNT"]}</td>
                          <td className="gf-num">{r["VOLUME (L)"].toLocaleString()}</td>
                          <td className="gf-num">${r.REVENUE.toFixed(2)}</td>
                          <td className="gf-num">${r["GROSS PROFIT"].toFixed(2)}</td>
                          <td className="gf-num">{r["COMMISSION CPL"].toFixed(2)}</td>
                          <td className="gf-num font-semibold">${r["COMMISSION ($)"].toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-zinc-300">
                      <tr className="font-semibold">
                        <td>TOTAL</td>
                        <td className="gf-num">{reportData.rows.reduce((a,r)=>a+r["TX COUNT"],0)}</td>
                        <td className="gf-num">{reportData.total_volume_L.toLocaleString()}</td>
                        <td></td><td></td><td></td>
                        <td className="gf-num text-green-700">${reportData.total_commission.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => { setReport(null); setReportData(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </SectionPad>
  );
}
